/**
 * Session history wire contract (PRD G2 Sessions tab; increment-8 plan §1 PR5).
 *
 * The per-episode sealed-session list — the `lastSealed` query GENERALIZED
 * (its `LIMIT 1` dropped): every session carrying a sign-off row, newest
 * first, slot-bound AND ad-hoc alike. The list is SIGN-OFF-driven, not
 * slot-driven — that is the deliberate difference from the calendar summary:
 * the calendar answers "what happened to each scheduled day" (including
 * missed, which this list never contains); this list answers "what records
 * exist", which is why an ad-hoc session appears here and nowhere on the
 * calendar grid.
 *
 * `assessmentAdministered` is FACTUAL, not scheduled: it says an assessment
 * head record names this session as its administration context — a sealed
 * session on an assessment day where no instrument was run does NOT carry the
 * suffix (the adopted prototype marks scheduled assessment days; ours marks
 * what happened, because this tab is the record trail).
 */

import { z } from 'zod';

import { SESSION_END_DISPOSITIONS } from './session';

export const sessionHistorySlotSchema = z.object({
  slotId: z.uuid(),
  slotDay: z.number().int().min(1),
  week: z.number().int().min(1),
  dayOfWeek: z.number().int().min(1),
  calendarDate: z.iso.date(),
  isAssessment: z.boolean(),
});
export type SessionHistorySlot = z.infer<typeof sessionHistorySlotSchema>;

export const sessionHistoryRowSchema = z.object({
  sessionId: z.uuid(),
  /** The sign-off's clinical instant — the list's order key (newest first). */
  sealedAt: z.iso.datetime(),
  signedOffBy: z.string().min(1),
  /** The sealed session-level note, when one was typed at confirmation. */
  sessionNote: z.string().nullable(),
  /** The bound slot's facts; null = ad-hoc (slotless) session. */
  slot: sessionHistorySlotSchema.nullable(),
  /** C9 — how the session ended. Null on pre-grammar ends. */
  endDisposition: z.enum(SESSION_END_DISPOSITIONS).nullable(),
  endDispositionReason: z.string().nullable(),
  /** An assessment head record was administered in this session (factual). */
  assessmentAdministered: z.boolean(),
});
export type SessionHistoryRow = z.infer<typeof sessionHistoryRowSchema>;

/** GET /api/episodes/:episodeId/sessions */
export const episodeSessionsResponseSchema = z.object({
  ok: z.literal(true),
  episodeId: z.uuid(),
  sessions: z.array(sessionHistoryRowSchema),
});
export type EpisodeSessionsResponse = z.infer<typeof episodeSessionsResponseSchema>;
